import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { SessionInfoSingleton } from "../data/singleton/session-info.singleton";
import { catchError } from "rxjs/operators";
import { AlertMessage } from "../constants/messages/alert-message";
import { Injectable } from "@angular/core";
import { formatMessage } from "src/app/shared/utils/message.util";

/**
 * パス「/sites」以下のAPIをコールするクラス
 */
@Injectable({
  providedIn: "root",
})
export class SitesApi {
  private readonly ERR_ALERT_TARGET_NAME = "施設情報";

  /**
   * コンストラクタ
   */
  constructor(
    private httpClient: HttpClient,
    private sessionInfoSingleton: SessionInfoSingleton
  ) {}

  /** GET */
  /**
   * 施設情報取得
   * - APIパス：/sites/sites_info_all/${companyId}
   * @param companyId
   * - "all"で全取得
   * @returns Observable<Object>型のレスポンス
   */
  getSitesInfo(companyId: string): Observable<Object> {
    const url = `${environment.apiUrl}/sites/sites_info_all/${companyId}`;
    const errorMessage = formatMessage(AlertMessage.FAILED_READ_ACTION, {
      target: this.ERR_ALERT_TARGET_NAME,
    });

    return this.httpClient
      .get(url, {
        headers: new HttpHeaders({
          Authorization: this.sessionInfoSingleton.getIdToken(),
          "Content-Type": "application/json",
        }),
      })
      .pipe(
        catchError((error) => {
          alert(errorMessage);
          return error;
        })
      );
  }

  /**
   * フロアの全件取得
   * - APIパス：/sites/${siteId}/floors
   * @param siteId 施設ID
   * @returns Observable<Object>型のレスポンス
   */
  getAllFloors(siteId: string): Observable<Object> {
    const url = `${environment.apiUrl}/sites/${siteId}/floors`;

    return this.httpClient
      .get(url, {
        headers: new HttpHeaders({
          Authorization: this.sessionInfoSingleton.getIdToken(),
          "Content-Type": "application/json",
        }),
      })
      .pipe(
        catchError((error) => {
          alert(AlertMessage.NOTFOUND_FLOOR_INFO);
          return error;
        })
      );
  }

  /**
   * 区画に紐付く全テナントの取得
   * - APIパス：/sites/${siteId}/partitions/tenants/${checkedMonth}
   * @param siteId 施設ID
   * @param checkedMonth 対象年月（yyyyMM）
   * @returns Observable<Object>型のレスポンス
   */
  getAllTenantsRelatedToParition(
    siteId: string,
    checkedMonth: string
  ): Observable<Object> {
    const url = `${environment.apiUrl}/sites/${siteId}/partitions/tenants/${checkedMonth}`;
    const errorMessage = formatMessage(AlertMessage.FAILED_READ_ACTION, {
      target: "テナント情報",
    });

    return this.httpClient
      .get(url, {
        headers: new HttpHeaders({
          Authorization: this.sessionInfoSingleton.getIdToken(),
          "Content-Type": "application/json",
        }),
      })
      .pipe(
        catchError((error) => {
          alert(errorMessage);
          return error;
        })
      );
  }

  /**
   * 施設内のメーター全件取得
   * - APIパス：/sites/${siteId}/meters_info_all
   * @param siteId 施設ID
   * @returns Observable<Object>型のレスポンス
   */
  getAllMetersRelatedToSites(siteId: string): Observable<Object> {
    const url = `${environment.apiUrl}/sites/${siteId}/meters_info_all`;
    const errorMessage = formatMessage(AlertMessage.FAILED_READ_ACTION, {
      target: "メーター情報",
    });

    return this.httpClient
      .get(url, {
        headers: new HttpHeaders({
          Authorization: this.sessionInfoSingleton.getIdToken(),
          "Content-Type": "application/json",
        }),
      })
      .pipe(
        catchError((error) => {
          alert(errorMessage);
          return error;
        })
      );
  }

  /**
   * レポート表示用の未確定データ取得
   * - APIパス：/sites/${siteId}/partitions/meters/${reportMonth}/value_confirmations/${lastMonthConfirmDate}
   * @param siteId 施設ID
   * @param reportMonth レポート年月（yyyyMM）
   * @param lastMonthConfirmDate 前月確定日（yyyyMMdd）
   * @returns Observable<Object>型のレスポンス
   */
  getPendingReportData(
    siteId: string,
    reportMonth: string,
    lastMonthConfirmDate: string
  ): Observable<Object> {
    const url = `${environment.apiUrl}/sites/${siteId}/partitions/meters/${reportMonth}/value_confirmations/${lastMonthConfirmDate}`;

    return this.httpClient.get(url, {
      headers: new HttpHeaders({
        Authorization: this.sessionInfoSingleton.getIdToken(),
        "Content-Type": "application/json",
      }),
    });
  }

  /**
   * 区画の全件取得
   * - APIパス：/sites/${siteId}/floors/partitions
   * @param siteId 施設ID
   * @returns Observable<Object>型のレスポンス
   */
  getAllPartitions(siteId: string): Observable<Object> {
    const url = `${environment.apiUrl}/sites/${siteId}/floors/partitions`;
    const errorMessage = formatMessage(AlertMessage.FAILED_READ_ACTION, {
      target: "区画情報",
    });

    return this.httpClient
      .get(url, {
        headers: new HttpHeaders({
          Authorization: this.sessionInfoSingleton.getIdToken(),
          "Content-Type": "application/json",
        }),
      })
      .pipe(
        catchError((error) => {
          alert(errorMessage);
          return error;
        })
      );
  }

  /**
   * 紐付情報を含む施設情報取得
   * - APIパス：/sites/user_site_links_info_all/${companyId}
   * @param companyId 会社コード
   * - "all"で全取得
   * @returns Observable<Object>型のレスポンス
   */
  getSiteWithUserLinks(companyId: string): Observable<Object> {
    const url = `${environment.apiUrl}/sites/user_site_links_info_all/${companyId}`;
    const errorMessage = formatMessage(AlertMessage.FAILED_READ_ACTION, {
      target: "紐付情報を含む施設情報",
    });

    return this.httpClient
      .get(url, {
        headers: new HttpHeaders({
          Authorization: this.sessionInfoSingleton.getIdToken(),
          "Content-Type": "application/json",
        }),
      })
      .pipe(
        catchError((error) => {
          alert(errorMessage);
          return error;
        })
      );
  }

  /** POST */
  /**
   * 施設情報作成
   * - APIパス：/sites/sites_info
   * @param params 内容
   * @returns Observable<Object>型のレスポンス
   */
  createSite(params: HttpParams): Observable<Object> {
    const url = `${environment.apiUrl}/sites/sites_info`;
    const errorMessage = formatMessage(AlertMessage.FAILED_CREATE_ACTION, {
      target: this.ERR_ALERT_TARGET_NAME,
    });

    return this.httpClient
      .post(url, params, {
        headers: new HttpHeaders({
          Authorization: this.sessionInfoSingleton.getIdToken(),
          "Content-Type": "application/x-www-form-urlencoded",
        }),
      })
      .pipe(
        catchError((error) => {
          alert(errorMessage);
          return error;
        })
      );
  }

  /** PUT,PATCH */
  /**
   * 施設情報削除
   * - APIパス：/sites/${siteId}/0/sites_info
   * @param siteId 施設ID
   * @param params 内容
   * @returns Observable<Object>型のレスポンス
   */
  deleteSite(siteId: string, params: HttpParams): Observable<Object> {
    const url = `${environment.apiUrl}/sites/${siteId}/0/sites_info`;
    const errorMessage = formatMessage(AlertMessage.FAILED_DELETE_ACTION, {
      target: this.ERR_ALERT_TARGET_NAME,
    });

    return this.httpClient
      .patch(url, params, {
        headers: new HttpHeaders({
          Authorization: this.sessionInfoSingleton.getIdToken(),
          "Content-Type": "application/x-www-form-urlencoded",
        }),
      })
      .pipe(
        catchError((error) => {
          alert(errorMessage);
          return error;
        })
      );
  }
  /**
   * 施設情報更新
   * - APIパス：/sites/${siteId}/1/sites_info
   * @param siteId 施設ID
   * @param params 内容
   * @returns Observable<Object>型のレスポンス
   */
  updateSite(siteId: string, params: HttpParams): Observable<Object> {
    const url = `${environment.apiUrl}/sites/${siteId}/1/sites_info`;
    const errorMessage = formatMessage(AlertMessage.FAILED_DELETE_ACTION, {
      target: this.ERR_ALERT_TARGET_NAME,
    });

    return this.httpClient
      .patch(url, params, {
        headers: new HttpHeaders({
          Authorization: this.sessionInfoSingleton.getIdToken(),
          "Content-Type": "application/x-www-form-urlencoded",
        }),
      })
      .pipe(
        catchError((error) => {
          alert(errorMessage);
          return error;
        })
      );
  }

  /**
   * 全フロアの表示順採番
   * - APIパス：/sites/${siteId}/floors/no
   * @param siteId フロアID
   * @param params 内容
   * @returns Observable<Object>型のレスポンス
   */
  allFloorsNumbering(siteId: string, params: HttpParams): Observable<Object> {
    const url = `${environment.apiUrl}/sites/${siteId}/floors/no`;

    return this.httpClient
      .patch(url, params, {
        headers: new HttpHeaders({
          Authorization: this.sessionInfoSingleton.getIdToken(),
          "Content-Type": "application/x-www-form-urlencoded",
        }),
      })
      .pipe(
        catchError((error) => {
          alert(AlertMessage.FAILED_NUMBERING);
          return error;
        })
      );
  }

  /**
   * 全区画の表示順採番
   * - APIパス：/sites/${siteId}/floors/partitions/no
   * @param siteId フロアID
   * @param params 内容
   * @returns Observable<Object>型のレスポンス
   */
  allPartitonsNumbering(
    siteId: string,
    params: HttpParams
  ): Observable<Object> {
    const url = `${environment.apiUrl}/sites/${siteId}/floors/partitions/no`;

    return this.httpClient
      .patch(url, params, {
        headers: new HttpHeaders({
          Authorization: this.sessionInfoSingleton.getIdToken(),
          "Content-Type": "application/x-www-form-urlencoded",
        }),
      })
      .pipe(
        catchError((error) => {
          alert(AlertMessage.FAILED_NUMBERING);
          return error;
        })
      );
  }

  /**
   * メーターの表示順採番
   * - APIパス：/sites/${siteId}/meter_info_all/no
   * @param siteId フロアID
   * @param params 内容
   * @returns Observable<Object>型のレスポンス
   */
  allMetersNumbering(siteId: string, params: HttpParams): Observable<Object> {
    const url = `${environment.apiUrl}/sites/${siteId}/meter_info_all/no`;

    return this.httpClient
      .patch(url, params, {
        headers: new HttpHeaders({
          Authorization: this.sessionInfoSingleton.getIdToken(),
          "Content-Type": "application/x-www-form-urlencoded",
        }),
      })
      .pipe(
        catchError((error) => {
          alert(AlertMessage.FAILED_NUMBERING);
          return error;
        })
      );
  }
}
